import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, of, catchError, tap, shareReplay } from 'rxjs';
import { environment } from '../../../environments/environment';
import { getStoredAuthToken } from '../auth/auth-token';

export interface MovieOfDayData {
  movie_id: number;
  title: string;
  poster_path: string | null;
  backdrop_path: string | null;
  overview: string | null;
  release_date: string | null;
  vote_average: number | null;
  vote_count: number | null;
  media_type: string;
  tagline: string | null;
  slug: string | null;
}

export interface MovieOfDayResponse {
  enabled: boolean;
  movie: MovieOfDayData | null;
  date: string;
}

export interface TmdbSearchResult {
  id: number;
  title: string;
  poster_path: string | null;
  backdrop_path: string | null;
  overview: string;
  release_date: string | null;
  vote_average: number | null;
  vote_count: number | null;
  media_type: string;
}

export interface MaintenanceStatus {
  maintenance_mode: boolean;
  maintenance_title: string;
  maintenance_message: string;
  maintenance_submessage: string;
  maintenance_end_time?: string | null;
  maintenance_allow_admin?: boolean;
}

export interface SiteSettings {
  quiz_enabled: boolean;
  blog_enabled: boolean;
  movie_of_the_day_enabled: boolean;
  actor_of_the_day_enabled?: boolean;
  featured_banner_enabled: boolean;
  maintenance_mode: boolean;
  maintenance_title: string;
  maintenance_message: string;
  maintenance_submessage: string;
  maintenance_end_time?: string | null;
  maintenance_allow_admin?: boolean;
}

@Injectable({ providedIn: 'root' })
export class SiteSettingsService {
  private readonly api = environment.backendUrl || '';

  constructor(private readonly http: HttpClient) {}

  private authHeaders(): HttpHeaders {
    const token = getStoredAuthToken();
    return new HttpHeaders({ Authorization: `Bearer ${token}` });
  }

  // ── Public ──

  getMovieOfDay(): Observable<MovieOfDayResponse> {
    return this.http.get<MovieOfDayResponse>(
      `${this.api}/api/admin/settings/movie-of-the-day`
    );
  }

  // ── Admin ──

  searchMovies(query: string): Observable<{ results: TmdbSearchResult[]; total: number }> {
    return this.http.get<{ results: TmdbSearchResult[]; total: number }>(
      `${this.api}/api/admin/settings/movie-of-the-day/search`,
      { params: { q: query } }
    );
  }

  setMovieOfDay(payload: MovieOfDayData): Observable<{ success: boolean; movie: MovieOfDayData }> {
    return this.http.put<{ success: boolean; movie: MovieOfDayData }>(
      `${this.api}/api/admin/settings/movie-of-the-day`,
      payload,
      { headers: this.authHeaders() }
    );
  }

  clearMovieOfDay(): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(
      `${this.api}/api/admin/settings/movie-of-the-day`,
      { headers: this.authHeaders() }
    );
  }

  getSiteSettings(): Observable<SiteSettings> {
    return this.http.get<SiteSettings>(
      `${this.api}/api/admin/settings/site`,
      { headers: this.authHeaders() }
    );
  }

  updateSiteSettings(payload: Partial<SiteSettings>): Observable<{ success: boolean }> {
    return this.http.put<{ success: boolean }>(
      `${this.api}/api/admin/settings/site`,
      payload,
      { headers: this.authHeaders() }
    );
  }

  // ── Maintenance Mode ──

  private readonly maintenanceStatus$ = new BehaviorSubject<MaintenanceStatus>({
    maintenance_mode: false,
    maintenance_title: 'The Scenetale',
    maintenance_message: '',
    maintenance_submessage: 'We are currently performing scheduled maintenance. Please check back shortly.',
    maintenance_end_time: null
  });

  readonly maintenanceStatusStream$: Observable<MaintenanceStatus> = this.maintenanceStatus$.asObservable();
  private maintenanceStatusCache$: Observable<MaintenanceStatus> | null = null;
  private lastMaintenanceFetchTime = 0;
  private readonly MAINTENANCE_CACHE_TTL_MS = 15_000; // 15s memory cache

  getMaintenanceStatus(forceRefresh = false): Observable<MaintenanceStatus> {
    const now = Date.now();
    if (!forceRefresh && this.maintenanceStatusCache$ && (now - this.lastMaintenanceFetchTime < this.MAINTENANCE_CACHE_TTL_MS)) {
      return this.maintenanceStatusCache$;
    }

    this.lastMaintenanceFetchTime = now;
    const url = forceRefresh
      ? `${this.api}/api/admin/settings/maintenance?force=true&_t=${now}`
      : `${this.api}/api/admin/settings/maintenance`;

    this.maintenanceStatusCache$ = this.http.get<MaintenanceStatus>(url).pipe(
      tap(status => this.maintenanceStatus$.next(status)),
      catchError(() => of(this.maintenanceStatus$.value)),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.maintenanceStatusCache$;
  }

  notifyMaintenanceModeOff(): void {
    const current = this.maintenanceStatus$.value;
    this.maintenanceStatusCache$ = null;
    this.maintenanceStatus$.next({
      ...current,
      maintenance_mode: false,
      maintenance_end_time: null
    });
  }

  getCurrentMaintenanceStatus(): MaintenanceStatus {
    return this.maintenanceStatus$.value;
  }
}
